import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "signup-form" }
const _hoisted_2 = { class: "form-title" }
const _hoisted_3 = {
  key: 0,
  class: "error-message"
}
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = { class: "already-member" }

import { reactive, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import AddPromoCode from '@/components/checkout/AddPromoCode.vue'
import gsInput from '@/components/form/gs-input.vue'
import { required, email, password } from '@/helpers/validation'
import { PRIVACY_POLICY_URL, TERMS_CONDITIONS_URL } from '@/helpers/constants'
import { getLocation } from '@/helpers/geolocation'
import { createAccount, usePromoCode, userRegistration } from '@api/index'
import { useRoute, useRouter } from 'vue-router'
import type { Currency } from '@/helpers/interfaces'

interface SignupFormInfo {
  name: string
  companyName: string
  email: string
  password: string
  vatNumber: string
  termsAndConditions: boolean
  promoCode?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SignupForm',
  emits: ["registered"],
  setup(__props, { emit: __emit }) {

const { t } = useI18n()
const store = useStore()
const route = useRoute()
const router = useRouter()
const emit = __emit

const valid = ref<boolean>(false)
const loading = ref<boolean>(false)
const showPassword = ref<boolean>(false)
const country = ref<string>()
getLocation()
  .then((c) => (country.value = c))
  .catch((e) => console.error("Error trying to retrieve the user's country", e))

store.commit('setEmailAlreadyUsed', false)
store.commit('setSignupComplete', false)
const emailAlreadyUsed = computed<boolean>(() => store.getters['getAccount'].emailAlreadyUsed)
const submitIsEnabled = computed<boolean>(
  () => valid.value && !emailAlreadyUsed.value && !loading.value,
)
const rules = {
  required,
  email,
  password,
}

const name =
  store.getters['getCurrentUser'].firstName || store.getters['getCurrentUser'].lastName
    ? `${store.getters['getCurrentUser'].firstName} ${store.getters['getCurrentUser'].lastName}`
    : ''
const form = reactive<SignupFormInfo>({
  name,
  companyName: store.getters['getAccount'].companyName || '',
  email: store.getters['getCurrentUser'].email || '',
  password: '',
  vatNumber: store.getters['getAccount'].vatId || '',
  termsAndConditions: false,
  promoCode: undefined,
})

const navigateToLogin = () => {
  return router.push({
    path: '/login',
    query: { app: '', withPublicToken: 'true', ...route.query },
  })
}

const submit = async () => {
  if (!submitIsEnabled.value) return
  loading.value = true
  try {
    await userRegistration({
      email: form.email,
      password: form.password,
      name: form.name,
    })
  } catch (error) {
    console.error('failed to register user', error)
    loading.value = false
    return
  }

  const account = store.getters['getAccount']
  if (!account.signupComplete) {
    loading.value = false
    return
  }

  const livePageId = Date.now() + Math.floor(Math.random() * 1000)
  let widgetFolderName = form.companyName + Math.floor(Math.random() * 100).toString()
  widgetFolderName = widgetFolderName.replace(/\s/g, '-').toLowerCase()

  const currentUser = store.getters['getCurrentUser']
  const [firstName, ...lastName] = form.name.split(' ')

  const payload = {
    user: {
      accountType: 'ecommerce',
      email: form.email,
      vatId: form.vatNumber,
      companyName: form.companyName,
      firstName: firstName,
      lastName: lastName.join(' '),
      accountId: currentUser.id,
      signupDate: Date.now(),
      termsAccept: form.termsAndConditions,
      currency: 'dollar' as Currency,
      livePageId: livePageId,
      widgetFolderName: widgetFolderName,
      accountFromMarketplace: true,
      address: {
        country: country.value || '',
      },
    },
  }

  await createAccount(payload)
  if (form.promoCode) await usePromoCode(form.promoCode)
  loading.value = false
  emit('registered')
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_gs_button = _resolveComponent("gs-button")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("h1", _hoisted_2, _toDisplayString(_unref(t)('create_account_title')), 1),
    _createVNode(_component_v_form, {
      modelValue: valid.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((valid).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(gsInput, {
          name: "name",
          modelValue: form.name,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.name) = $event)),
          label: `${_unref(t)('form_label_name')}*`,
          autocomplete: "name",
          "hide-details": false,
          rules: [rules.required],
          type: "text"
        }, null, 8, ["modelValue", "label", "rules"]),
        _createVNode(gsInput, {
          name: "company-name",
          modelValue: form.companyName,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.companyName) = $event)),
          label: `${_unref(t)('form_label_company_name')}*`,
          autocomplete: "company",
          "hide-details": false,
          rules: [rules.required],
          type: "text"
        }, null, 8, ["modelValue", "label", "rules"]),
        _createVNode(gsInput, {
          name: "email",
          modelValue: form.email,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.email) = $event)),
          label: `${_unref(t)('form_label_email')}*`,
          autocomplete: "email",
          "validate-on": "blur",
          "hide-details": false,
          rules: [rules.required, rules.email],
          onInput: _cache[3] || (_cache[3] = () => _unref(store).commit('setEmailAlreadyUsed', false)),
          type: "email"
        }, null, 8, ["modelValue", "label", "rules"]),
        _createVNode(gsInput, {
          name: "password",
          modelValue: form.password,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.password) = $event)),
          class: "password-field",
          label: `${_unref(t)('form_label_password')}*`,
          autocomplete: "password",
          hint: _unref(t)('form_hint_password_complexity'),
          "hide-details": false,
          rules: [rules.required, rules.password],
          "append-inner-icon": showPassword.value ? 'mdi-eye' : 'mdi-eye-off',
          "persistent-hint": true,
          type: showPassword.value ? 'text' : 'password',
          "onClick:appendInner": _cache[5] || (_cache[5] = ($event: any) => (showPassword.value = !showPassword.value))
        }, null, 8, ["modelValue", "label", "hint", "rules", "append-inner-icon", "type"]),
        (emailAlreadyUsed.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(_component_v_icon, null, {
                default: _withCtx(() => _cache[11] || (_cache[11] = [
                  _createTextVNode("mdi-information-outline")
                ])),
                _: 1
              }),
              _createElementVNode("div", null, [
                _createTextVNode(_toDisplayString(_unref(t)('form_email_alread_used')) + " ", 1),
                _createElementVNode("span", {
                  class: "login-link",
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$router.push('/login?app')))
                }, _toDisplayString(_ctx.$t('login')), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        _createVNode(gsInput, {
          id: "vat-number",
          name: "vat-number",
          modelValue: form.vatNumber,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.vatNumber) = $event)),
          label: _unref(t)('form_label_vat_number'),
          autocomplete: "vat",
          "hide-details": false,
          type: "text"
        }, null, 8, ["modelValue", "label"]),
        _createVNode(_component_v_checkbox, {
          id: "terms-and-conditions",
          name: "terms-and-conditions",
          class: "terms-and-conditions-input",
          modelValue: form.termsAndConditions,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.termsAndConditions) = $event)),
          density: "compact",
          "hide-details": true,
          rules: [rules.required]
        }, {
          label: _withCtx(() => [
            _createElementVNode("span", {
              innerHTML: 
              `${_unref(t)('form_label_terms_and_conditions', {
                termsAndConditionsUrl: _unref(TERMS_CONDITIONS_URL),
                privacyPolicyUrl: _unref(PRIVACY_POLICY_URL),
              })}*`
            
            }, null, 8, _hoisted_4)
          ]),
          _: 1
        }, 8, ["modelValue", "rules"]),
        _createVNode(AddPromoCode, {
          class: "promo-code-trigger",
          onSetPromoCode: _cache[9] || (_cache[9] = (promoCode) => form.promoCode = promoCode)
        }),
        _createVNode(_component_gs_button, {
          size: "large",
          type: "primary",
          "full-width": "",
          disabled: !submitIsEnabled.value,
          loading: loading.value,
          onClick: _withModifiers(submit, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('create_account_cta')), 1)
          ]),
          _: 1
        }, 8, ["disabled", "loading"]),
        _createElementVNode("div", _hoisted_5, [
          _createTextVNode(_toDisplayString(_unref(t)('already_member')) + " ", 1),
          _createElementVNode("span", {
            class: "green-text",
            onClick: navigateToLogin
          }, _toDisplayString(_unref(t)('login')), 1)
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})